import * as api from '../api';
import { actionTypes } from 'redux-resource';
import { SubmissionError } from 'redux-form';
import { PER_PAGE } from 'helpers/actions';

export const fetchAllUsers =
  (page = 1, childAccounts, userId, ordering) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'users',
      requestKey: childAccounts
        ? `fetchChildAccounts${page}`
        : `fetchAll${page}`, // this is used elsewhere so beware when changing
      list: childAccounts
        ? `pageChildAccounts${page}_${userId}`
        : `page${page}`,
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .fetchAllUsers(page, childAccounts, userId, ordering)
      .then((response) => {
        const { count, next, previous, results } = response.data;
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: results || [],
          requestProperties: {
            perPage: (results && results.length) || PER_PAGE,
            count,
            next,
            previous,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
      });
  };

export const findUser = (userId) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'users',
    requestKey: 'find',
    resources: [userId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .findUser(userId)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
      dispatch({
        type: 'UPDATE_RESOURCES',
        resources: {
          addresses: response.data.address,
        },
      });
      return response.data;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
        statusCode: err.response && err.response.status,
      });
    });
};

export const saveUser =
  ({ id, ...attrs }, method = 'PUT') =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'users',
      requestKey: 'save',
      resources: [id],
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.UPDATE_RESOURCES_PENDING,
    });

    let apiPromise;
    if (method === 'PATCH') {
      apiPromise = api.saveUserPatch(id, attrs);
    } else {
      apiPromise = api.saveUser(id, attrs);
    }

    return apiPromise
      .then((response) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
          resources: [response.data],
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.UPDATE_RESOURCES_FAILED,
        });

        let messages = err.response
          ? err.response.data
          : { detail: 'Something went wrong' };
        throw new SubmissionError(messages);
      });
  };

export const saveUserField =
  ({ id, ...attrs }) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'users',
      requestKey: 'saveUserField',
      resources: [id],
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.UPDATE_RESOURCES_PENDING,
    });

    return api
      .saveUserField(id, attrs)
      .then((response) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
          resources: [response.data],
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.UPDATE_RESOURCES_FAILED,
        });

        let messages = err.response
          ? err.response.data
          : { detail: 'Something went wrong' };
        throw new SubmissionError(messages);
      });
  };

export const createUser = (attrs) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'users',
    requestKey: 'create',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.CREATE_RESOURCES_PENDING,
  });

  return api
    .createUser(attrs)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.CREATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.CREATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const createChildUser = (attrs) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'users',
    requestKey: 'createChildUser',
  }

  dispatch({
    ...actionDefaults,
    type: actionTypes.CREATE_RESOURCES_PENDING
  });

  return api.createUser(attrs).then(response => {
    dispatch({
      ...actionDefaults,
      type: actionTypes.CREATE_RESOURCES_SUCCEEDED,
      resources: [response.data]
    });

  }).catch(err => {
    dispatch({
      ...actionDefaults,
      type: actionTypes.CREATE_RESOURCES_FAILED
    });

    let messages = err.response ? err.response.data : { detail: 'Something went wrong' }
    throw new SubmissionError(messages);
  });
}

export const searchUsers =
  (page = 1, search) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'users',
      requestKey: `searchUsers${page}`, // this is used elsewhere so beware when changing
      list: `searchPage${page}`,
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    let searchValues = { ...search };
    if (search.email) {
      searchValues.email = encodeURIComponent(search.email);
    }

    return api
      .searchUsers(page, searchValues)
      .then((response) => {
        const { count, next, previous, results } = response.data;
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: results || [],
          requestProperties: {
            perPage: (results && results.length) || PER_PAGE,
            count,
            next,
            previous,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
      });
  };

export const updateUserCreator =
  ({ id, ...attrs }) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'users',
      requestKey: 'updateCreator',
      resources: [id],
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.UPDATE_RESOURCES_PENDING,
    });

    return api
      .updateUserCreator(id, attrs)
      .then((response) => {
        const { created_by } = response.data;
        dispatch({
          ...actionDefaults,
          type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
          resources: [{ id, created_by }],
        });
        return { success: true };
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.UPDATE_RESOURCES_FAILED,
        });

        let messages = err.response
          ? err.response.data
          : { detail: 'Something went wrong' };
        throw new SubmissionError(messages);
      });
  };

export const setTaxExempt = (id, taxExempt) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'users',
    requestKey: 'setTaxExempt',
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .setTaxExempt(id, taxExempt)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const toggleUserIsActive = (id, status) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'users',
    requestKey: `toggleUserIsActive_${id}`,
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .toggleUserIsActive(id, status)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      const messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
        requestProperties: {
          error: messages,
        },
      });
    });
};

export const getUsersApiOptions = () => {
  return api
    .fetchUsersApiOptions()
    .then((res) => res.data.actions.GET)
    .catch((e) => ({ error: "Api isn't available" }));
};

export const updateSimoCycleType = (id, cycleType) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'users',
    requestKey: 'updateSimoCycleType',
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .updateSimoCycleType(id, cycleType)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (messages.detail) {
        throw new SubmissionError({ _error: messages });
      } else if (messages.message) {
        throw new SubmissionError({ _error: messages.message });
      } else {
        throw new SubmissionError(messages);
      }
    });
};

export const updateSimoAccounts = (id, params) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'users',
    requestKey: 'updateSimoAccountName',
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .updateSimoAccounts(id, params)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (messages.detail || messages.error) {
        throw new SubmissionError({ _error: messages });
      } else if (messages.message) {
        throw new SubmissionError({ _error: messages.message });
      } else {
        throw new SubmissionError(messages);
      }
    });
};
